<template>
  
  <div class="properties-container">

    <v-tabs
      v-if      = "properties"
      color     = "white"
      background-color = "rgba(0, 0, 0, 0.3)"
      dark
      v-model   = "tab"
    >
      <v-tab 
        v-show    = "containerProperties.length > 0"
        key       = "container"
      >
        Container
      </v-tab>
      
      <v-tab 
        v-show    = "videoTracks.length > 0"
        key       = "video"
      >
        Video
      </v-tab>
      
      <v-tab 
        v-show    = "audioTracks.length > 0"
        key       = "audio"
      >
        Audio
      </v-tab>
      <v-tab 
        v-show    = "subtitleTracks.length > 0"
        key       = "metadata"
      >
        Metadata
      </v-tab>
    </v-tabs>

    <div class="tabs-content-container">

      <div class="property-browser">

        <!-- The "tabs" use v-show rather than v-if so that tree state is not lost. -->

        <!-- Container -->
        <PropertyTree
          ref         = "container_tree"
          class       = "contain tree"
          v-show      = "tab === 0"
          :properties = "containerProperties"
        >
        </PropertyTree>

        <!-- Video -->

        <v-list 
          v-if    = "tab === 1"
          class   = "selector"
          dense
        >
          <v-list-item-group 
            v-model   = "videoTrackIndex"
            mandatory
          >
            <v-list-item 
              v-for     = "index in videoTracks"
              :key      = "index"
            >
              Track {{ index + 1 }}
            </v-list-item>
          </v-list-item-group>
        </v-list>

        <PropertyTree
          ref         = "video_tree"
          class       = "contain tree"
          v-show      = "tab === 1"
          :properties = "videoProperties"
        >
        </PropertyTree>

        <!-- Audio -->

        <v-list 
          v-if    = "tab === 2"
          class   = "selector"
          dense
        >
          <v-list-item-group 
            v-model   = "audioTrackIndex"
            mandatory
          >
            <v-list-item 
              v-for     = "index in audioTracks"
              :key      = "index"
            >
              Track {{ index + 1 }}
            </v-list-item>
          </v-list-item-group>
        </v-list>

        <PropertyTree
          ref         = "audio_tree"
          class       = "contain tree"
          v-show      = "tab === 2"
          :properties = "audioProperties"
        >
        </PropertyTree>

        <!-- Metadata -->

        <v-list 
          v-if    = "tab === 3"
          class   = "selector"
          dense
        >
          <v-list-item-group 
            v-model   = "subtitleTrackIndex"
            mandatory
          >
            <v-list-item 
              v-for     = "index in subtitleTracks"
              :key      = "index"
            >
              Track {{ index + 1 }}
            </v-list-item>
          </v-list-item-group>
        </v-list>

        <PropertyTree
          ref         = "subtitle_tree"
          class       = "contain tree"
          v-show      = "tab === 3"
          :properties = "subtitleProperties"
        >
        </PropertyTree>

      </div>

    </div>

    <v-toolbar
      v-if        = "properties"
      dense
      class       = "flex-grow-0"
      elevation   = "0"
      color       = "rgba(0, 0, 0, 0.3)"
    >

      <v-spacer></v-spacer>

      <ToolbarButton
        tooltip   = "Expand All Rows"
        @click    = "onExpandAll"
      >
        <v-icon>mdi-expand-all</v-icon>
      </ToolbarButton>

      <ToolbarButton
        tooltip   = "Collapse All Rows"
        @click    = "onCollapseAll"
      >
        <v-icon>mdi-collapse-all</v-icon>
      </ToolbarButton>

      <v-divider vertical></v-divider>

      <ToolbarButton
        tooltip   = "Export All Properties"
        @click    = "onExport"
      >
        <v-icon>mdi-export</v-icon>
      </ToolbarButton>

    </v-toolbar>

  </div>
  
</template>

<script lang="js">

// #region Imports

/* Telestream */
import PropertyTree from './components/properties/PropertyTree';
import ToolbarButton from './components/buttons/ToolbarButton';
import { splitPath } from '@/components/file-toolbar/utilities/path.utils';

// #endregion Imports

export default {

  // #region Properties

  name:  'PropertiesPane',
  props: {
    properties: Object,
    path:       String
  },
  data() {
    return {
      tab:                'container',
      videoTrackIndex:    0,
      audioTrackIndex:    0,
      subtitleTrackIndex: 0,
    };
  },
  computed: {
    containerProperties() {
      if (!this.properties) {
        return [];
      }

      return this.properties.containerProperties;
    },
    videoTracks() {
      const tracks = [];

      if (this.properties) {
        for (let index = 0; index < this.properties.videoProperties.length; index++) {
          tracks.push(index);
        }
      }

      return tracks;
    },
    videoProperties() {
      if (
        !this.properties ||
        !this.properties.videoProperties[this.videoTrackIndex]
      ) {
        return [];
      }

      return this.properties.videoProperties[this.videoTrackIndex];
    },
    audioTracks() {
      const tracks = [];

      if (this.properties) {
        for (let index = 0; index < this.properties.audioProperties.length; index++) {
          tracks.push(index);
        }
      }

      return tracks;
    },
    audioProperties() {
      if (
        !this.properties || 
        !this.properties.audioProperties[this.audioTrackIndex]
      ) {
        return [];
      }
      
      return this.properties.audioProperties[this.audioTrackIndex];
    },
    subtitleTracks() {
      const tracks = [];

      if (this.properties) {
        for (let index = 0; index < this.properties.subtitleProperties.length; index++) {
          tracks.push(index);
        }
      }

      return tracks;
    },
    subtitleProperties() {
      if (
        !this.properties || 
        !this.properties.subtitleProperties[this.subtitleTrackIndex]
      ) {
        return [];
      }

      return this.properties.subtitleProperties[this.subtitleTrackIndex];
    }
  },
  components: {
    PropertyTree,
    ToolbarButton
  },
  watch: {
    path() {
      this.tab = 0;
      this.videoTrackIndex = 0;
      this.audioTrackIndex = 0;
      this.subtitleTrackIndex = 0;
    }
  },

  // #endregion Properties

  // #region Operations

  methods: {

    // #region Events

    onExpandAll() {
      switch (this.tab) {

      case 0: this.$refs.container_tree.expandAll(); break;
      case 1: this.$refs.video_tree.expandAll(); break;
      case 2: this.$refs.audio_tree.expandAll(); break;
      case 3: this.$refs.subtitle_tree.expandAll(); break;

      }
    },

    onCollapseAll() {
      switch (this.tab) {

      case 0: this.$refs.container_tree.collapseAll(); break;
      case 1: this.$refs.video_tree.collapseAll(); break;
      case 2: this.$refs.audio_tree.collapseAll(); break;
      case 3: this.$refs.subtitle_tree.collapseAll(); break;
      
      }
    },

    onExport() {
      const serializedContent = JSON.stringify(this.properties, null, 2);
      const filename = splitPath(this.path)
        .filename
        .split('.')[0];

      const link = document.createElement('a');
      link.download = `${filename}.json`;
      link.href = `data:text/plain;charset=utf-8,${encodeURIComponent(serializedContent)}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    // #endregion Events

  }

  // #endregion Operations

};

</script>

<style lang="scss" scoped>

.properties-container {
  display:        flex;
  flex-direction: column;
  overflow:       hidden;

  .v-tabs {
    flex:           0;
  }

  /*
   * Putting the tab content in an absolutely positioned container is a bit
   * goofy, but the only way I can get the content not to mess with the overall
   * layout when the tree gets really big.
   */
  .tabs-content-container {
    flex:           1;
    position:       relative;
    height:         100%;
    
    .property-browser {
      position:     absolute;
      top:          0;
      left:         0;
      right:        0;
      bottom:       0;
      
      display:        flex;
      flex-direction: row;
      
      .selector {
        padding:      8px;
        overflow-y:   scroll;
        min-width:    160px;
      }

      .tree {
        padding:      8px;
        flex:         1;
        overflow:     auto;
      }
    }
  }
}

</style>